import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LocationApiService } from '@xpo-ltl-2.0/sdk-location';
import { SdkBillCorrectionModule } from '@xpo-ltl-2.0/sdk-billcorrection';
import { ConfigManagerModule, CONFIG_MANAGER_SERVICE_CONFIGURATION } from '@xpo-ltl/config-manager';
import { DataApiService, NotificationService } from '@xpo-ltl/data-api';
import { XpoLtlAuthConfigLoaderService, XpoLtlAuthGuard, XpoLtlAuthModule } from '@xpo-ltl/ngx-auth';
import { FirestoreAuthGuard } from './shared/guards/firestore-auth/firestore-auth.guard';
import { SdkPricingModule } from '@xpo-ltl/sdk-pricing';
import { AcAutomationApiService } from '@xpo-ltl/sdk-acautomation';
import { SdkUserPreferenceModule } from '@xpo-ltl/sdk-userpreference';
import { XpoAuthConfig, XPO_AUTH_CONFIG } from '@xpo/ngx-auth';
import { environment } from '../environments/environment';
import { NotificationComponent } from './app-notification.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { StorageACService } from './shared/services/storage-ac/storage-ac.service';
import { YesNoPipe } from './shared/pipes/yes-no/yes-no.pipe';
import { CurrencyPipe, DatePipe, LowerCasePipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { CommonModule } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { UserPreferencesService } from './services/user-preferences/user-preferences.service';
import { SupportedAppEnum, XpoMaintenanceModule, XpoMaintAppConfig } from '@xpo-ltl/maint-ui';
import { AcAutomationService } from './shared/services/ac-auto/ac-automation.service';
import { XpoButtonModule } from '@xpo-ltl/ngx-ltl-core/button';
import { XpoCardModule } from '@xpo-ltl/ngx-ltl-core/card';
import { XpoDialogModule } from '@xpo-ltl/ngx-ltl-core/dialog';
import { XpoShellModule } from '@xpo-ltl/ngx-ltl-core/shell';
import { XpoSnackBarModule } from '@xpo-ltl/ngx-ltl-core/snack-bar';
import { XpoApplicationSwitcherModule } from '@xpo-ltl/ngx-ltl-core/application-switcher';
import { XpoIconModule } from '@xpo-ltl/ngx-ltl-core/icon';
import { XpoFooterModule } from '@xpo-ltl/ngx-ltl-core/footer';
import { AppStoreModule } from './app-store/app-store.module';
import { XpoLtlAuthUiModule } from '@xpo-ltl/ngx-auth-ui';
import { UserPreferenceDataApiService } from './services/user-preferences-redux/user-preferences-data-api.service';

export function authConfigFactory(configService: XpoLtlAuthConfigLoaderService): XpoAuthConfig {
  configService.initSettings('./assets/config.json');

  return new XpoAuthConfig({
    appCode: configService.appCode,
    scopes: configService.scopes,
    isProd: configService.isProd,
    applicationRootUri: configService.appRootUri,
  });
}

@NgModule({
  declarations: [AppComponent, NotificationComponent],
  imports: [
    CommonModule,
    AngularFireModule.initializeApp(environment.firebase),
    BrowserModule,
    ConfigManagerModule,
    AppStoreModule,
    SharedModule,
    XpoLtlAuthUiModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    XpoButtonModule,
    XpoCardModule,
    XpoDialogModule,
    XpoShellModule,
    MatMenuModule,
    XpoSnackBarModule,
    SdkUserPreferenceModule,
    XpoMaintenanceModule.initialize({ appName: SupportedAppEnum.AC_AUTO } as XpoMaintAppConfig),
    XpoLtlAuthModule.forRoot({
      provide: XPO_AUTH_CONFIG,
      useFactory: authConfigFactory,
      deps: [XpoLtlAuthConfigLoaderService],
    }),
    AppRoutingModule,
    // XpoPageNotFoundRoutingModule,
    XpoApplicationSwitcherModule,
    SdkPricingModule,
    SdkBillCorrectionModule,
    XpoIconModule,
    XpoFooterModule,
    FlexLayoutModule,
  ],
  providers: [
    // Use the same guard instance for routing and the root component's startup signal.
    { provide: XpoLtlAuthGuard, useExisting: FirestoreAuthGuard },
    {
      provide: CONFIG_MANAGER_SERVICE_CONFIGURATION,
      useValue: { configFileUrl: './assets/config.json' },
    },
    DataApiService,
    LocationApiService,
    NotificationService,
    StorageACService,
    UserPreferencesService,
    AcAutomationService,
    AcAutomationApiService,
    UserPreferenceDataApiService,
    // Pipes
    DatePipe,
    YesNoPipe,
    CurrencyPipe,
    UpperCasePipe,
    TitleCasePipe,
    LowerCasePipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {}
